import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import { Reply, ThumbsDown, ThumbsUp } from "lucide-react"
import { Separator } from "@components/common/separator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { LIKE_NOTE_ENDPOINT, SERVER_URL, UNLIKE_NOTE_ENDPOINT } from "@pma/utils/api-routes"
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import { useEffect, useState } from "react"
import { useAuth } from "@pma/stores/auth-store"

interface Props {
  note: DictType
  showReply: CallableFunction
  likes: any
}

const NoteActions = ({ note, showReply, likes }: Props) => {
  const { translate, locale } = useTranslator()
  const navigate = useNavigate()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { authConfig } = useAuth()
  const userId = authConfig?.id
  const [likeCount, setLikeCount] = useState<number | string>("")
  const [isLiked, setIsLiked] = useState(
    likes?.find((like: any) => like?.id === userId) ? true : false,
  )

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  useEffect(() => {
    const liked = likes.find((like: any) => like?.id === userId)
    setLikeCount(likes.length || "")
    setIsLiked(liked ? true : false)
  }, [likes])

  const likeNote = async (noteId: number) => {
    const response = await axios.post(
      SERVER_URL + LIKE_NOTE_ENDPOINT,
      { note_id: noteId },

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const unlikeNote = async (noteId: number) => {
    const response = await axios.post(
      SERVER_URL + UNLIKE_NOTE_ENDPOINT,
      { note_id: noteId },

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const reactToNoteMutation = useMutation({
    mutationKey: ["react-to-note"],
    mutationFn: (like: boolean) => reactToNotification(like),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`task-notes-${note.task_id}`] })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  const isHebrew = locale === "he"

  const reactToNotification = async (like: boolean) => {
    let response

    if (like) {
      response = await unlikeNote(note.id)
    } else {
      response = await likeNote(note.id)
    }

    return response.data
  }

  const handleLike = () => {
    isLiked
      ? setLikeCount((prev: number | string) =>
          prev === 1 ? 0 : prev !== 0 ? Number(prev) - 1 : "",
        )
      : setLikeCount((prev: number | string) => Number(prev) + 1)
    reactToNoteMutation.mutate(isLiked)
  }

  return (
    <div
      className={cn(
        "my-2 flex h-[30px] w-full items-center justify-around border-y",
        isHebrew ? "left-0 right-auto" : "left-auto right-0",
      )}
    >
      <Tooltip>
        <TooltipTrigger>
          <Reply
            size={12}
            className='text-slate-500 hover:scale-105'
            onClick={() => showReply((prev: boolean) => !prev)}
          />
          <TooltipContent className='border bg-white text-black shadow'>
            {translate("Reply")}
          </TooltipContent>
        </TooltipTrigger>
      </Tooltip>
      <Separator orientation='vertical' />
      {/* <Tooltip>
        <TooltipTrigger> */}
      <div className='flex flex-row items-center'>
        <ThumbsUp
          size={12}
          className={`cursor-pointer text-slate-500 hover:scale-105 ${isLiked ? "text-blue-700" : ""}`}
          //   TODO - Follow up backend
          onClick={handleLike}
        />
        <small>{likeCount || ""}</small>
      </div>
      {/* <TooltipContent className="">{translate("Like")}</TooltipContent>
        </TooltipTrigger>
      </Tooltip> */}
    </div>
  )
}

export default NoteActions
